import { MouseEvent, ReactElement, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  'cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-px-4 cr-py-2 cr-shadow-md cr-transition-colors cr-duration-200 disabled:cr-opacity-50',
  {
    variants: {
      variant: {
        outline:
          'cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-font-header cr-uppercase cr-text-blue-950 hover:cr-bg-cirrusBlue-50 hover:cr-text-blue-700 disabled:cr-bg-cirrusBlue-100 disabled:cr-text-blue-950',
        secondary:
          'cr-bg-cirrusBlue-100 cr-text-blue-900 cr-border-2 cr-border-cirrusBlue-100 cr-font-semibold hover:cr-bg-gray-350 hover:cr-border-gray-350',
        primary:
          'cr-bg-cirrusBlue-100 cr-text-blue-900/70 cr-font-semibold cr-no-underline hover:cr-text-blue-900 hover:cr-bg-cirrusBlue-75',
        ghost:
          'cr-bg-transparent cr-text-blue-900 hover:cr-bg-gray-100',
        danger:
          'cr-border-2 cr-border-solid cr-border-red-800 cr-bg-red-100 cr-font-header cr-uppercase cr-text-red-800 hover:cr-bg-red-200 hover:cr-text-red-900',
      },
      size: {
        sm: 'cr-px-3 cr-py-1.5 cr-text-sm',
        // compact: sm padding with body text size — the standard for in-page action buttons
        compact: 'cr-px-3 cr-py-1.5 cr-text-base',
        md: 'cr-px-4 cr-py-2 cr-text-base',
        lg: 'cr-px-6 cr-py-3 cr-text-lg',
      },
      normalCase: {
        true: 'cr-normal-case',
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'md',
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

interface ButtonProps extends ButtonVariantProps {
  /** Optional for type="submit" buttons, where the form's onSubmit handles the action. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  children?: ReactNode;
  icon?: ReactElement;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export { buttonVariants };

export default function Button({
  onClick,
  children,
  icon,
  className = '',
  disabled = false,
  variant,
  size,
  normalCase,
  type = 'button',
}: ButtonProps): ReactElement {
  return (
    <button
      className={cn(buttonVariants({ variant, size, normalCase }), className)}
      onClick={onClick}
      type={type}
      disabled={disabled}
    >
      {children} {icon}
    </button>
  );
}
